import { Flex, SimpleGrid } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useCallback } from "react";

import ActionIcon from "../../../components/ActionIcon/ActionIcon.tsx";
import MultiFieldCard from "../../../components/Cards/MultiFieldCard/MultiFieldCard.tsx";
import Edit from "../../../components/Icons/Edit.tsx";
import {
  UpdateCustomerInput,
  UpdateCustomerRiskMonitoringRemarksInput,
} from "../../../graphql/generated";
import { useCurrentUserContext } from "../../../providers/CurrentUserProvider.tsx";
import { Customer, RiskMonitoringRemarks } from "../../../types/customer.ts";
import { CustomerCollectionSummary } from "../../../types/customerCollectionSummary.ts";
import { UserOption } from "../../../types/user.ts";
import { isBababosTenant } from "../../../utils/user.ts";
import { useRiskMonitoringCard } from "../../ClikDataDetails/CompanyInfo/hooks/useRiskMonitoringCard.tsx";
import { useStyles } from "./ActivityManagementCards.styles.ts";
import { CreateRiskMonitoringModal } from "./CreateRiskMonitoringModal.tsx";
import { useCollectionSummaryInfo } from "./hooks/useCollectionSummaryInfo.tsx";
import { useCustomerStatusInfo } from "./hooks/useCustomerStatusInfo.tsx";

interface ActivityManagementCardsProps {
  customer: { data: Partial<Customer>; loading: boolean };
  collectionSummary: {
    data: Partial<CustomerCollectionSummary>;
    loading: boolean;
  };
  riskMonitoring: {
    data: RiskMonitoringRemarks | null | undefined;
    loading: boolean;
  };
  collectors: UserOption[];
  onChangeCustomer: (value: Partial<UpdateCustomerInput>) => void;
  onChangeRiskMonitoring: (
    value: UpdateCustomerRiskMonitoringRemarksInput
  ) => void;
}

const ActivityManagementCards = ({
  customer,
  collectionSummary,
  collectors,
  riskMonitoring,
  onChangeCustomer,
  onChangeRiskMonitoring,
}: ActivityManagementCardsProps) => {
  const { classes } = useStyles();

  const { user } = useCurrentUserContext();

  const customerStatusInfo = useCustomerStatusInfo({ customer: customer.data });
  const collectionSummaryInfo = useCollectionSummaryInfo({
    collectionSummary: collectionSummary.data,
    collectors,
    customer: customer.data,
    onChange: onChangeCustomer,
  });
  const riskMonitoringInfo = useRiskMonitoringCard({
    data: riskMonitoring.data,
  });

  const [
    riskMonitoringModalOpened,
    { open: openRiskMonitoringModal, close: closeRiskMonitoringModal },
  ] = useDisclosure(false);

  const handleChangeRiskMonitoring = useCallback(
    (value: UpdateCustomerRiskMonitoringRemarksInput) => {
      onChangeRiskMonitoring(value);
      closeRiskMonitoringModal();
    },
    [closeRiskMonitoringModal, onChangeRiskMonitoring]
  );

  return (
    <>
      <SimpleGrid
        cols={3}
        spacing={20}
        verticalSpacing={16}
        className={classes.columnsWrapper}
      >
        <MultiFieldCard
          label="Customer statuses"
          items={customerStatusInfo}
          loading={customer.loading}
        />
        <MultiFieldCard
          label="Collection Summary"
          items={collectionSummaryInfo}
          loading={collectionSummary.loading}
        />
        {isBababosTenant(user) && (
          <MultiFieldCard
            label="Risk Monitoring"
            items={riskMonitoringInfo}
            loading={riskMonitoring.loading}
            cardControl={
              <Flex gap={8}>
                <ActionIcon spaces={false} onClick={openRiskMonitoringModal}>
                  <Edit />
                </ActionIcon>
              </Flex>
            }
          />
        )}
      </SimpleGrid>
      {riskMonitoringModalOpened && (
        <CreateRiskMonitoringModal
          data={riskMonitoring.data}
          opened={true}
          onClose={closeRiskMonitoringModal}
          onSubmit={handleChangeRiskMonitoring}
        />
      )}
    </>
  );
};

export default ActivityManagementCards;
